import CommitmentFilterType from "@/constants/CommitmentFilterType";
import OutcomeSortType from "@/constants/OutcomeSortType";
import Nprogress from "nprogress";
import VueNotifications from "vue-notifications";
import { i18n } from "@/i18n";
import { mapOutcomeBody } from "@/store/modules/outcomeHelpers";
import { API_V2, teamAPI } from "@/api";
import { applyPatches, upcat } from "@/lib/nodash";

const COMMITMENT_PAGE_SIZE = 20;

const pristinePage = () => {
	return {
		offset: 0,
		hasMorePages: true,
		commitments: [],
	};
};

const pristineState = () => {
	return {
		teamId: "",
		userProfile: {},
		timezone: "",
		metadata: {
			[ CommitmentFilterType.COMPLETE ]: 0,
			[ CommitmentFilterType.DUE_IN_NEXT_WEEK ]: 0,
			[ CommitmentFilterType.NO_DUE_DATE ]: 0,
			[ CommitmentFilterType.OUTSTANDING ]: 0,
			[ CommitmentFilterType.OVERDUE ]: 0,
		},
		pageGroups: {
			[ CommitmentFilterType.COMPLETE ]: pristinePage(),
			[ CommitmentFilterType.DUE_IN_NEXT_WEEK ]: pristinePage(),
			[ CommitmentFilterType.NO_DUE_DATE ]: pristinePage(),
			[ CommitmentFilterType.OUTSTANDING ]: pristinePage(),
			[ CommitmentFilterType.OVERDUE ]: pristinePage(),
		},
		selectedFilter: CommitmentFilterType.OUTSTANDING,
		isInitializingStore: true,
		isFetchingMetadata: true,
		isPagingCommitments: true,
		hasPagedCommitments: false,
		isUpdatingCommitment: false,
	};
};

const _state = pristineState();

const _getters = {
	commitments: ( state /* getters, rootState, rootGetters */ ) => {
		const { commitments, } = state.pageGroups[ state.selectedFilter ];
		return commitments; // should already be sorted correctly
	},
	metadata: ( state /* getters, rootState, rootGetters */ ) => {
		return state.metadata;
	},
	selectedFilter: ( state /* getters, rootState, rootGetters */ ) => {
		return state.selectedFilter;
	},
	hasMorePages: ( state /* getters, rootState, rootGetters */ ) => {
		return state.pageGroups[ state.selectedFilter ].hasMorePages;
	},
	isInitializingStore: ( state /* getters, rootState, rootGetters */ ) => {
		return state.isInitializingStore;
	},
	isFetchingMetadata: ( state /* getters, rootState, rootGetters */ ) => {
		return state.isFetchingMetadata;
	},
	isPagingCommitments: ( state /* getters, rootState, rootGetters */ ) => {
		return state.isPagingCommitments;
	},
	isUpdatingCommitment: ( state /* getters, rootState, rootGetters */ ) => {
		return state.isUpdatingCommitment;
	},
};

const mutations = {
	toggleFlag: ( state, { flag, value, } ) => {
		state[ flag ] = value;
	},
	onStoreInitialized: ( state, initializedState ) => {
		Object.assign( state, initializedState );
	},
	onSetCommitmentFilter: ( state, { commitmentFilterType, } ) => {
		state.selectedFilter = commitmentFilterType;
	},
	onMetadataChanged: ( state, { metadata, } ) => {
		state.metadata = {
			[ CommitmentFilterType.COMPLETE ]: metadata.completed,
			[ CommitmentFilterType.DUE_IN_NEXT_WEEK ]: metadata.dueInNextWeek,
			[ CommitmentFilterType.NO_DUE_DATE ]: metadata.noDueDate,
			[ CommitmentFilterType.OUTSTANDING ]: metadata.outstanding,
			[ CommitmentFilterType.OVERDUE ]: metadata.overdue,
		};
	},
	onPageGroupChanged: ( state, { selectedFilter, pageGroup, } ) => {
		state.pageGroups = {
			...state.pageGroups,
			[ selectedFilter ]: pageGroup,
		};
		state.hasPagedCommitments = true;
	},
};

const actions = {
	initializeStore: async ( { commit, dispatch, /* state, dispatch, rootGetters, rootState, */ }, { teamId, userProfile, timezone, } ) => {
		try {
			Nprogress.start();
			commit( "toggleFlag", { flag: "isInitializingStore", value: true, } );
			const initializedState = Object.assign( {}, pristineState(), { teamId, userProfile, timezone, } );
			commit( "onStoreInitialized", initializedState );
			dispatch( "fetchMetadata" );
			dispatch( "pageCommitments" );
		} catch ( e ) {
			const message = i18n.t( "store.teamCommitments_initializationError", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		} finally {
			Nprogress.done();
			commit( "toggleFlag", { flag: "isInitializingStore", value: false, } );
		}
	},
	fetchMetadata: async ( { commit, state, /* dispatch, rootGetters, rootState, */ } ) => {
		try {
			Nprogress.start();
			commit( "toggleFlag", { flag: "isFetchingMetadata", value: true, } );
			const { teamId, } = state;
			const nextMetadata = await teamAPI.fetchCommitmentMetadata( { teamId, } );
			commit( "onMetadataChanged", { metadata: nextMetadata, } );
		} catch ( e ) {
			const message = i18n.t( "store.teamCommitments_fetchMetadataError", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		} finally {
			commit( "toggleFlag", { flag: "isFetchingMetadata", value: false, } );
			Nprogress.done();
		}
	},
	pageCommitments: async ( { commit, state, dispatch, /* rootGetters, rootState, */ }, { forceReset, } = { forceReset: false, } ) => {
		try {
			const {
				teamId,
				selectedFilter,
				pageGroups,
				hasPagedCommitments,
				isPagingCommitments,
			} = state;

			if ( hasPagedCommitments && isPagingCommitments ) {
				return;
			}

			const oldPageGroup = pageGroups[ selectedFilter ];

			if ( !oldPageGroup.hasMorePages && !forceReset ) {
				return;
			}

			Nprogress.start();
			commit( "toggleFlag", { flag: "isPagingCommitments", value: true, } );

			const nextCommitmentSet = await teamAPI.fetchCommitments( teamId, {
				filterType: selectedFilter,
				limit: COMMITMENT_PAGE_SIZE,
				offset: forceReset ? 0 : ( oldPageGroup.offset * COMMITMENT_PAGE_SIZE ),
				sortType: selectedFilter === CommitmentFilterType.COMPLETE ?
					OutcomeSortType.COMPLETED_RECENTLY :
					OutcomeSortType.OUTCOME_IMPORTANCE,
			} );

			const hasMorePages = nextCommitmentSet.length === COMMITMENT_PAGE_SIZE;

			let newPageGroup;
			if ( forceReset ) {
				newPageGroup = {
					hasMorePages,
					offset: nextCommitmentSet.length ? 1 : 0,
					commitments: nextCommitmentSet,
				};
			} else {
				newPageGroup = {
					hasMorePages,
					offset: nextCommitmentSet.length ? oldPageGroup.offset + 1 : oldPageGroup.offset,
					commitments: upcat(
						oldPageGroup.commitments,
						nextCommitmentSet,
						( a, b ) => a.id === b.id
					),
				};
			}

			commit( "onPageGroupChanged", { selectedFilter, pageGroup: newPageGroup, } );

			dispatch( "fetchMetadata" );
		} catch ( e ) {
			const message = i18n.t( "store.teamCommitments_pageCommitmentsError", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		} finally {
			commit( "toggleFlag", { flag: "isPagingCommitments", value: false, } );
			Nprogress.done();
		}
	},
	updateCommitment: async ( { commit, state, dispatch, /* rootState, */ }, { commitment, patches, } ) => {
		try {
			Nprogress.start();
			commit( "toggleFlag", { flag: "isUpdatingCommitment", value: true, } );

			const { selectedFilter, pageGroups, } = state;

			const oldPageGroup = pageGroups[ selectedFilter ];

			// first, pretend it worked...
			const patchedCommitment = applyPatches( commitment, patches );

			let newPageGroup = {
				...oldPageGroup,
				commitments: upcat(
					oldPageGroup.commitments,
					[ patchedCommitment, ],
					( a, b ) => a.id === b.id
				),
			};

			commit( "onPageGroupChanged", { selectedFilter, pageGroup: newPageGroup, } );

			// then, actually make it work!
			const mappedCommitment = mapOutcomeBody( patchedCommitment, "utc", API_V2 );
			const updatedCommitment = await teamAPI.updateCommitment( {
				commitment: mappedCommitment,
				meetingId: patchedCommitment.meetingId,
			} );

			const removeCompletedCommitment = updatedCommitment.completed &&
				[
					CommitmentFilterType.DUE_IN_NEXT_WEEK,
					CommitmentFilterType.NO_DUE_DATE,
					CommitmentFilterType.OUTSTANDING,
					CommitmentFilterType.OVERDUE,
				].includes( selectedFilter );

			const removeUncompletedCommitment = !updatedCommitment.completed && selectedFilter === CommitmentFilterType.COMPLETE;

			if ( removeCompletedCommitment || removeUncompletedCommitment ) {
				// remove the commitment as its state has changed
				newPageGroup = {
					...oldPageGroup,
					commitments: oldPageGroup.commitments.filter( c => c.id !== updatedCommitment.id ),
				};
			} else {
				// otherwise replace the commitment in the collection
				newPageGroup = {
					...oldPageGroup,
					commitments: upcat(
						oldPageGroup.commitments,
						[ updatedCommitment, ],
						( a, b ) => a.id === b.id
					),
				};
			}

			commit( "onPageGroupChanged", { selectedFilter, pageGroup: newPageGroup, } );

			dispatch( "fetchMetadata" );
		} catch ( e ) {
			const message = i18n.t( "store.teamCommitments_updateCommitmentError", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		} finally {
			commit( "toggleFlag", { flag: "isUpdatingCommitment", value: false, } );
			Nprogress.done();
		}
	},
	filterCommitments: async ( { commit, dispatch, /* state, rootGetters, rootState, */ }, { commitmentFilterType, } ) => {
		commit( "onSetCommitmentFilter", { commitmentFilterType, } );
		dispatch( "pageCommitments", { forceReset: true, } );
	},
};

export default {
	state: _state,
	getters: _getters,
	mutations,
	actions,
	namespaced: true,
};



// WEBPACK FOOTER //
// ./src/store/modules/teamCommitments/index.js